"use client";

import React from "react";
import { PORTAL_THEME } from "../../lib/portal_theme";
import {
  REGGIE_COSTS, REGGIE_ACTION_LABELS, REGGIE_MONTHLY, REGGIE_TIER_LABELS, REGGIE_PACKS,
  REGGIE_OPEN_EVENT, REGGIE_CHANGED_EVENT,
} from "../../lib/reggie_credits_fields.js";
import { SUBSCRIPTION_PRICE } from "../../lib/subscription_fields.js";
import Spinner from "./Spinner";

const T = PORTAL_THEME;

/** Open the Reggie Credits pop-up from anywhere (e.g. after a 402 out_of_credits). */
export function openReggieCredits() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(REGGIE_OPEN_EVENT));
}

/** Refresh the header balance after a call that may have spent credits. */
export function reggieCreditsChanged() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(REGGIE_CHANGED_EVENT));
}

/** For fal-backed fetches: opens the pop-up when out of credits, otherwise refreshes the balance. */
export function noteReggieResponse(data) {
  if (data?.error === "out_of_credits") openReggieCredits();
  else reggieCreditsChanged();
}

const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric" });

// "Reggie Credits" in the header's top-right corner (StoreHeader). The
// pop-up explains what they pay for, shows the month's allowance and bought
// balance, and sells packs through Shopify checkout (lib/reggie_credits.js).
export default function ReggieCredits() {
  const [credits, setCredits] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [buying, setBuying] = React.useState("");
  const [error, setError] = React.useState("");

  const load = React.useCallback(() => {
    fetch("/api/reggie-credits", { cache: "no-store", credentials: "include" })
      .then((r) => r.json()).then((d) => { if (d?.ok) setCredits(d); }).catch(() => {});
  }, []);

  React.useEffect(() => {
    load();
    const onOpen = () => { setOpen(true); load(); };
    window.addEventListener(REGGIE_OPEN_EVENT, onOpen);
    window.addEventListener(REGGIE_CHANGED_EVENT, load);
    window.addEventListener("focus", load);
    return () => {
      window.removeEventListener(REGGIE_OPEN_EVENT, onOpen);
      window.removeEventListener(REGGIE_CHANGED_EVENT, load);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const buy = async (pack) => {
    if (!credits?.signedIn) {
      window.location.href = `/signin?noGuest=1&returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return;
    }
    setBuying(pack); setError("");
    try {
      const res = await fetch("/api/reggie-credits", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d?.checkoutUrl) throw new Error(d?.error || `Failed (${res.status})`);
      window.location.href = d.checkoutUrl;
    } catch (e) { setError(String(e?.message || e)); setBuying(""); }
  };

  const count = !credits ? null : credits.unlimited ? "∞" : credits.total;

  return (
    <>
      <style>{`
        .reggie-pill { position: absolute; top: 14px; right: 16px; }
        @media (max-width: 640px) { .reggie-pill { position: static; margin: 10px auto 0; width: fit-content; } }
      `}</style>
      <button type="button" className="reggie-pill" onClick={() => { setOpen(true); load(); }}
        aria-label="Reggie Credits"
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 999, cursor: "pointer",
          background: "transparent", border: "1px solid rgba(200,200,200,0.45)", boxShadow: "0 0 12px 2px rgba(200,200,200,0.15)",
          color: "#ede8e0", fontFamily: T.fontFamily, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
        }}>
        <span style={{
          minWidth: 22, height: 22, padding: "0 6px", borderRadius: 999, boxSizing: "border-box",
          background: T.chromeGradient, color: T.chromeButtonText, fontSize: 12, fontWeight: 800,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{count ?? "RG"}</span>
        Reggie Credits
      </button>

      {open && (
        <div role="dialog" aria-modal="true" aria-label="Reggie Credits" onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 440, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxSizing: "border-box",
              background: T.cardBackground, border: T.cardEdge, boxShadow: "none",
              borderRadius: 0, padding: "22px 20px", color: T.text, fontFamily: T.fontFamily, textAlign: "left",
              display: "flex", flexDirection: "column", gap: 14,
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Reggie Credits</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                style={{ background: "transparent", border: "none", color: T.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: T.textMuted }}>
              Reggie Credits power our <strong style={{ color: T.text }}>Uniform Swap</strong> and{" "}
              <strong style={{ color: T.text }}>Sky Removal</strong> features. Everything else in the editor is free to use.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
              {Object.entries(REGGIE_COSTS).map(([action, cost]) => (
                <div key={action} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{REGGIE_ACTION_LABELS[action]}</span>
                  <span style={{ fontWeight: 700 }}>{cost} credit{cost === 1 ? "" : "s"}</span>
                </div>
              ))}
              <div style={{ color: T.textMuted, fontSize: 12 }}>A photo whose sky we&apos;ve already removed is free. Failed attempts are refunded.</div>
            </div>

            {!credits?.unlimited && (
            <div style={{ borderTop: `1px solid ${T.cardBorder}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
              {!credits ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.textMuted }}><Spinner size={14} /> Loading your credits…</div>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>You have {credits.total} credit{credits.total === 1 ? "" : "s"}</div>
                  <div style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.6 }}>
                    {credits.monthlyLeft} of {credits.allowance} free this month ({REGGIE_TIER_LABELS[credits.tier]}) — refills {fmtDate(credits.resetsAt)}.
                    {credits.purchased > 0 && <> Plus {credits.purchased} bought credit{credits.purchased === 1 ? "" : "s"}, which never expire.</>}
                  </div>
                </>
              )}
            </div>
            )}

            {credits && !credits.unlimited && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Buy credits</div>
                  {REGGIE_PACKS.map((p) => (
                    <button key={p.id} type="button" disabled={!!buying} onClick={() => buy(p.id)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 16px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "1px",
                        background: T.chromeGradient, border: T.chromeBorder, boxShadow: T.chromeButtonShadow, color: T.chromeButtonText,
                        fontFamily: T.fontFamily, fontWeight: 700, fontSize: 14, cursor: buying ? "wait" : "pointer", opacity: buying && buying !== p.id ? 0.6 : 1,
                      }}>
                      <span>{buying === p.id ? "Redirecting to checkout…" : `${p.credits} credits`}</span>
                      <span>${p.price}</span>
                    </button>
                  ))}
                  {!credits.signedIn && <div style={{ color: T.textMuted, fontSize: 12 }}>Sign in to buy credits — they&apos;re saved to your account.</div>}
                  {error && <div style={{ color: T.error, fontSize: 13 }}>{error}</div>}
                </div>

                <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.6 }}>
                  Free each month: guests {REGGIE_MONTHLY.guest}, accounts {REGGIE_MONTHLY.member}, subscribers {REGGIE_MONTHLY.subscriber}.
                  {credits.tier !== "subscriber" && <> <a href="/subscription" style={{ color: T.accentOrange, fontWeight: 700, textDecoration: "none" }}>Subscribe</a> for {SUBSCRIPTION_PRICE}/mo to get {REGGIE_MONTHLY.subscriber} a month plus unlimited Instant Graphics.</>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
